import { EDITOR_TYPES } from './TextEditor.ts';

export const PATHWAY_TAB_TYPES = {
  REFERENCED: 'referenced',
  MANAGED: 'managed'
} as const;

export const PATHWAY_TYPE = {
  PHASED: 'phased',
  BUNDLED: 'bundled'
} as const;

/**
 * The contexts a file can be in
 */
export const FILE_CONTEXTS = {
  EDIT: 'edit',
  TEST: 'test',
  LIVE: 'live'
} as const;

/**
 * Types of snapshots that can be created for files
 */
export const SNAPSHOT_TYPES = {
  MANUAL: 'manual',
  PUBLISH_LIVE: 'publish_live',
  PUBLISH_TEST: 'publish_test',
  SCHEMA_UPGRADE: 'schema_upgrade',
  RESTORE: 'restore',
  AFTER_EDIT_SESSION_TIMEOUT: 'after_edit_session_timeout'
} as const;

/**
 * All file types
 */
export const FILE_TYPES = {
  AUTOMATION: 'automation',
  PATHWAY: 'pathway',
  FLOWCHART: 'flowchart',
  CALCULATOR: 'calculator',
  DOCUMENT: 'document',
  PDF: 'pdf',
  WEBLINK: 'weblink'
} as const;

/** How authors see each file type and publish context in Studio tables, filters and pickers. */
export const FILE_TYPE_LABELS: Record<(typeof FILE_TYPES)[keyof typeof FILE_TYPES], string> = {
  [FILE_TYPES.AUTOMATION]: 'Automation',
  [FILE_TYPES.CALCULATOR]: 'Calculator',
  [FILE_TYPES.DOCUMENT]: 'Document',
  [FILE_TYPES.FLOWCHART]: 'Flowchart',
  [FILE_TYPES.PATHWAY]: 'Pathway',
  [FILE_TYPES.PDF]: 'PDF',
  [FILE_TYPES.WEBLINK]: 'Weblink'
};

export const FILE_CONTEXT_LABELS: Record<
  (typeof FILE_CONTEXTS)[keyof typeof FILE_CONTEXTS],
  string
> = {
  [FILE_CONTEXTS.EDIT]: 'Draft',
  [FILE_CONTEXTS.TEST]: 'Test',
  [FILE_CONTEXTS.LIVE]: 'Live'
};

// File types nested under a parent pathway module, which the pathway author app opens.
const PATHWAY_AUTHOR_FILE_TYPES: readonly string[] = [
  FILE_TYPES.PATHWAY,
  FILE_TYPES.PDF,
  FILE_TYPES.DOCUMENT,
  FILE_TYPES.WEBLINK
];

/** The flowchart editor opens its Overview card on the section this query param names. */
export const OVERVIEW_SECTION_QUERY_PARAM = 'overview';

/** Where an author edits a file; a flowchart link can open its Overview on one section. */
export function getAuthorFileLink({
  fileType,
  moduleId,
  fileId,
  overviewSection
}: {
  fileType: string;
  moduleId: string;
  fileId: string;
  overviewSection?: string;
}) {
  if (fileType === FILE_TYPES.FLOWCHART) {
    const query = overviewSection
      ? `?${OVERVIEW_SECTION_QUERY_PARAM}=${encodeURIComponent(overviewSection)}`
      : '';

    return `/flowcharts/author/modules/${moduleId}/files/${fileId}${query}`;
  }

  if (PATHWAY_AUTHOR_FILE_TYPES.includes(fileType)) {
    return `/pathways/author/modules/${moduleId}/files/${fileId}`;
  }

  // Unknown future file types open the meta viewer, which resolves any file shape.
  return `/meta/viewer/modules/${moduleId}/files/${fileId}`;
}

/**
 * Possible values for when to file question responses at
 */
export const EHR_RESPONSES_TIMESTAMP_FROM = {
  SESSION: 'session',
  CLICK: 'click'
} as const;

// Recall is resolved from the file alone, so a new file has to carry its own value from the start.
export const INITIAL_EHR_RECALL = {
  enabled: true,
  patientScoped: true,
  encounterScoped: true,
  practitionerScoped: false
} as const;

// How a file with no stored recall behaves at runtime — deliberately not INITIAL_EHR_RECALL, so
// authoring shows recall as off for such a file rather than implying it is on.
export const UNSET_EHR_RECALL = {
  enabled: false,
  patientScoped: true,
  encounterScoped: true,
  practitionerScoped: false
} as const;

/**
 * All file viewing modes
 */
export const FILE_VIEW_MODES = {
  AUTHOR: 'author',
  VIEWER: 'viewer',
  EHR: 'ehr',
  REVIEWER: 'reviewer',
  STATIC: 'static'
} as const;

/** File types with no author route — link to the viewer route instead. */
export const VIEWER_ONLY_FILE_TYPES = [FILE_TYPES.PDF, FILE_TYPES.WEBLINK] as const;

/** File types with a reviewer route; reviewers lack the MFA the viewer routes require. */
export const REVIEWER_ROUTED_FILE_TYPES = [
  FILE_TYPES.FLOWCHART,
  FILE_TYPES.PATHWAY,
  FILE_TYPES.CALCULATOR
] as const;

/** Manager types that route a reviewer through their own app instead of the managed file's
 * (web-flowchart's reviewer redirect and the reviewer homepage link both follow these). */
export const PATHWAY_REVIEWER_MANAGER_TYPES = [
  FILE_TYPES.PATHWAY,
  FILE_TYPES.PDF,
  FILE_TYPES.DOCUMENT,
  FILE_TYPES.WEBLINK
] as const;

/**
 * Maximum file size for image uploads in bytes (10MB)
 * Matches backend limit in apps/cdn-origin/src/utils/extractFormData.js
 */
export const MAX_IMAGE_FILE_SIZE = 10485760;

export const FILE_TAG_STATUS = {
  SUGGESTED: 'suggested',
  LIVE: 'live'
} as const;

export const FILE_TAG_STATUSES = [FILE_TAG_STATUS.SUGGESTED, FILE_TAG_STATUS.LIVE] as const;

export type FileTagStatus = (typeof FILE_TAG_STATUSES)[number];

/** Maps a publish target status to the required source status */
export const FILE_TAG_PUBLISH_RULES: Partial<Record<FileTagStatus, FileTagStatus>> = {
  [FILE_TAG_STATUS.LIVE]: FILE_TAG_STATUS.SUGGESTED
} as const;

/** Maps an unpublish source status to the resulting target status */
export const FILE_TAG_UNPUBLISH_RULES: Partial<Record<FileTagStatus, FileTagStatus>> = {
  [FILE_TAG_STATUS.LIVE]: FILE_TAG_STATUS.SUGGESTED
} as const;

/**
 * Owner types for rows in the file element index (see FileElementIndex model).
 */
export const FILE_ELEMENT_OWNER_TYPE = {
  NODE: 'node',
  EDGE: 'edge',
  QUESTIONS: 'questions'
} as const;
export type FileElementOwnerType =
  (typeof FILE_ELEMENT_OWNER_TYPE)[keyof typeof FILE_ELEMENT_OWNER_TYPE];

/**
 * Sub-owner types for rows in the file element index.
 */
export const FILE_ELEMENT_SUB_OWNER_TYPE = {
  QUESTION: 'question',
  ACTION: 'action',
  CRITERION: 'criterion',
  POPUP: 'popup'
} as const;
export type FileElementSubOwnerType =
  (typeof FILE_ELEMENT_SUB_OWNER_TYPE)[keyof typeof FILE_ELEMENT_SUB_OWNER_TYPE];

/**
 * Data types for rows in the file element index.
 */
export const FILE_ELEMENT_DATA_TYPE = {
  ORDER: 'order',
  DATA_ELEMENT: 'dataElement',
  QUESTION: 'question',
  FILE_LINK: 'fileLink',
  WEBLINK: 'weblink',
  POPUP: 'popup',
  DESTINATION: 'destination',
  ACTION: 'action',
  CRITERION: 'criterion'
} as const;
export type FileElementDataType =
  (typeof FILE_ELEMENT_DATA_TYPE)[keyof typeof FILE_ELEMENT_DATA_TYPE];

/** Status of a single reviewer's participation in a FileReview. Drives the
 *  "My Status" column on the reviewer homepage and the "X of N reviewed"
 *  rollup on the deferred Studio Reviewer tab.
 *
 *  Transitions (enforced in the resource layer, not the schema):
 *    not_started   -> in_progress   (auto on first file open)
 *    in_progress   -> review_complete (explicit click)
 *    review_complete -> in_progress (reviewer re-opens)
 *    *             -> not_started   (author re-invites; same row, reset) */
export const FILE_REVIEW_INVITE_STATUSES = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  REVIEW_COMPLETE: 'review_complete'
} as const;
export type FileReviewInviteStatus =
  (typeof FILE_REVIEW_INVITE_STATUSES)[keyof typeof FILE_REVIEW_INVITE_STATUSES];

/**
 * Which comment emails someone gets for a file under review.
 *   every       every new comment on the file
 *   my_threads  only comments in threads the person has commented in
 *   none        nothing; comment counts still show in Sent for Review
 */
export const REVIEW_COMMENT_EMAIL_MODES = {
  EVERY: 'every',
  MY_THREADS: 'my_threads',
  NONE: 'none'
} as const;
export type ReviewCommentEmailMode =
  (typeof REVIEW_COMMENT_EMAIL_MODES)[keyof typeof REVIEW_COMMENT_EMAIL_MODES];

/** A sender's per-file mode until they choose: every comment, today's behavior for owners. */
export const SENDER_DEFAULT_EMAIL_MODE = REVIEW_COMMENT_EMAIL_MODES.EVERY;

/** A reviewer's per-file mode until someone changes it: replies in threads they commented in. */
export const REVIEWER_DEFAULT_EMAIL_MODE = REVIEW_COMMENT_EMAIL_MODES.MY_THREADS;

/**
 * The sections of a pathway overview, and the order they render in.
 *
 * Order is a static rule rather than stored data: authors cannot reorder sections, so both the
 * author preview and the Atlas card iterate this list instead of reading an order off the file.
 */
export const FILE_OVERVIEW_SECTIONS = {
  INCLUSION: 'inclusion',
  OUTCOMES_POPULATION: 'outcomesPopulation',
  GOALS: 'goals',
  METRICS: 'metrics',
  REFERENCES: 'references',
  CONTRIBUTORS: 'contributors',
  TAGS: 'tags',
  REVIEW_AND_APPROVAL: 'reviewAndApproval',
  PUBLISHED: 'published',
  OWNERS: 'owners',
  REVIEWERS: 'reviewers',
  ANTICIPATED_RELEASE: 'anticipatedRelease',
  NOTES: 'notes',
  WORKFLOW_STATUS: 'workflowStatus'
} as const;
export type FileOverviewSectionKey =
  (typeof FILE_OVERVIEW_SECTIONS)[keyof typeof FILE_OVERVIEW_SECTIONS];

/** The sections whose bodies are authored content in `data.overview`. */
export const FILE_OVERVIEW_SECTION_ORDER = [
  FILE_OVERVIEW_SECTIONS.INCLUSION,
  FILE_OVERVIEW_SECTIONS.GOALS,
  FILE_OVERVIEW_SECTIONS.OUTCOMES_POPULATION,
  FILE_OVERVIEW_SECTIONS.METRICS,
  FILE_OVERVIEW_SECTIONS.REFERENCES,
  FILE_OVERVIEW_SECTIONS.CONTRIBUTORS,
  FILE_OVERVIEW_SECTIONS.NOTES
] as const satisfies readonly FileOverviewSectionKey[];
export type FileOverviewContentSectionKey = (typeof FILE_OVERVIEW_SECTION_ORDER)[number];

/** Every overview section in the fixed order organizations and authors see; no one reorders it. */
export const FILE_OVERVIEW_FIXED_SECTION_ORDER = [
  FILE_OVERVIEW_SECTIONS.INCLUSION,
  FILE_OVERVIEW_SECTIONS.GOALS,
  FILE_OVERVIEW_SECTIONS.METRICS,
  FILE_OVERVIEW_SECTIONS.REFERENCES,
  FILE_OVERVIEW_SECTIONS.CONTRIBUTORS,
  FILE_OVERVIEW_SECTIONS.TAGS,
  FILE_OVERVIEW_SECTIONS.REVIEW_AND_APPROVAL,
  FILE_OVERVIEW_SECTIONS.OWNERS,
  FILE_OVERVIEW_SECTIONS.REVIEWERS,
  FILE_OVERVIEW_SECTIONS.ANTICIPATED_RELEASE,
  FILE_OVERVIEW_SECTIONS.PUBLISHED,
  FILE_OVERVIEW_SECTIONS.OUTCOMES_POPULATION,
  FILE_OVERVIEW_SECTIONS.NOTES,
  FILE_OVERVIEW_SECTIONS.WORKFLOW_STATUS
] as const satisfies readonly FileOverviewSectionKey[];

/**
 * Sections no reader renders: Outcomes population is for AgileMD's own analysis. The author card
 * offers it like any other section, it is always stored hidden, and it does not count toward
 * whether an overview has content.
 */
export const FILE_OVERVIEW_AUTHOR_ONLY_SECTIONS: readonly FileOverviewSectionKey[] = [
  FILE_OVERVIEW_SECTIONS.OUTCOMES_POPULATION
];

/**
 * Names for each overview body, one per metric category. The author editor and the AI suggestion
 * step both address a body by this key; web-flowchart's `utils/yjs/overview.ts` maps it to where
 * the body lives in the Y.Doc.
 */
export const FILE_OVERVIEW_Y_KEYS = {
  INCLUSION: 'inclusion',
  OUTCOMES_POPULATION: 'outcomesPopulation',
  GOALS: 'goals',
  REFERENCES: 'references',
  METRICS_OUTCOME: 'metricsOutcome',
  METRICS_PROCESS: 'metricsProcess',
  METRICS_BALANCING: 'metricsBalancing',
  CONTRIBUTORS: 'contributors',
  NOTES: 'notes'
} as const;
export type FileOverviewYKey = (typeof FILE_OVERVIEW_Y_KEYS)[keyof typeof FILE_OVERVIEW_Y_KEYS];

export const FILE_OVERVIEW_Y_KEY_ORDER = [
  FILE_OVERVIEW_Y_KEYS.INCLUSION,
  FILE_OVERVIEW_Y_KEYS.GOALS,
  FILE_OVERVIEW_Y_KEYS.OUTCOMES_POPULATION,
  FILE_OVERVIEW_Y_KEYS.REFERENCES,
  FILE_OVERVIEW_Y_KEYS.METRICS_OUTCOME,
  FILE_OVERVIEW_Y_KEYS.METRICS_PROCESS,
  FILE_OVERVIEW_Y_KEYS.METRICS_BALANCING,
  FILE_OVERVIEW_Y_KEYS.CONTRIBUTORS,
  FILE_OVERVIEW_Y_KEYS.NOTES
] as const satisfies readonly FileOverviewYKey[];

/** The bodies AI suggest reads and writes; Contributors and Notes are only ever author-written. */
export const FILE_OVERVIEW_SUGGESTIBLE_Y_KEYS = [
  FILE_OVERVIEW_Y_KEYS.INCLUSION,
  FILE_OVERVIEW_Y_KEYS.GOALS,
  FILE_OVERVIEW_Y_KEYS.OUTCOMES_POPULATION,
  FILE_OVERVIEW_Y_KEYS.REFERENCES,
  FILE_OVERVIEW_Y_KEYS.METRICS_OUTCOME,
  FILE_OVERVIEW_Y_KEYS.METRICS_PROCESS,
  FILE_OVERVIEW_Y_KEYS.METRICS_BALANCING
] as const satisfies readonly FileOverviewYKey[];
export type FileOverviewSuggestibleYKey = (typeof FILE_OVERVIEW_SUGGESTIBLE_Y_KEYS)[number];

/** Metric categories, at most one body each, rendered in this order under fixed headings. */
export const FILE_OVERVIEW_METRIC_CATEGORIES = {
  OUTCOME: 'outcome',
  PROCESS: 'process',
  BALANCING: 'balancing'
} as const;
export type FileOverviewMetricCategory =
  (typeof FILE_OVERVIEW_METRIC_CATEGORIES)[keyof typeof FILE_OVERVIEW_METRIC_CATEGORIES];

export const FILE_OVERVIEW_METRIC_CATEGORY_ORDER = [
  FILE_OVERVIEW_METRIC_CATEGORIES.OUTCOME,
  FILE_OVERVIEW_METRIC_CATEGORIES.PROCESS,
  FILE_OVERVIEW_METRIC_CATEGORIES.BALANCING
] as const satisfies readonly FileOverviewMetricCategory[];

/**
 * Headings the reader sees; the author preview and the Atlas card must agree on these.
 *
 * The `inclusion` key keeps its spelling while its label reads "Intended for", so the wording can
 * change again without a data migration. Criteria wording belongs to population-criteria.
 */
export const FILE_OVERVIEW_SECTION_LABELS = {
  [FILE_OVERVIEW_SECTIONS.INCLUSION]: 'Intended for',
  [FILE_OVERVIEW_SECTIONS.OUTCOMES_POPULATION]: 'Outcomes population',
  [FILE_OVERVIEW_SECTIONS.GOALS]: 'Goals',
  [FILE_OVERVIEW_SECTIONS.METRICS]: 'Metrics',
  [FILE_OVERVIEW_SECTIONS.REFERENCES]: 'References',
  [FILE_OVERVIEW_SECTIONS.CONTRIBUTORS]: 'Contributors',
  [FILE_OVERVIEW_SECTIONS.TAGS]: 'Tags',
  [FILE_OVERVIEW_SECTIONS.REVIEW_AND_APPROVAL]: 'Review and approval',
  [FILE_OVERVIEW_SECTIONS.PUBLISHED]: 'Published',
  [FILE_OVERVIEW_SECTIONS.OWNERS]: 'Owners',
  [FILE_OVERVIEW_SECTIONS.REVIEWERS]: 'Reviewers',
  [FILE_OVERVIEW_SECTIONS.ANTICIPATED_RELEASE]: 'Anticipated release',
  [FILE_OVERVIEW_SECTIONS.NOTES]: 'Notes',
  [FILE_OVERVIEW_SECTIONS.WORKFLOW_STATUS]: 'Workflow status'
} as const satisfies Record<FileOverviewSectionKey, string>;

export interface FileOverviewSectionSetting {
  defaultEnabled: boolean;
  defaultHidden: boolean;
  requiredBeforePublish: boolean;
}

/** What a section starts as for an organization that has not saved its own overview settings. */
export const FILE_OVERVIEW_RECOMMENDED_SECTION_SETTINGS = {
  [FILE_OVERVIEW_SECTIONS.INCLUSION]: {
    defaultEnabled: true,
    defaultHidden: false,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.GOALS]: {
    defaultEnabled: true,
    defaultHidden: false,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.METRICS]: {
    defaultEnabled: true,
    defaultHidden: false,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.REFERENCES]: {
    defaultEnabled: true,
    defaultHidden: false,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.CONTRIBUTORS]: {
    defaultEnabled: true,
    defaultHidden: false,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.TAGS]: {
    defaultEnabled: true,
    defaultHidden: true,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.REVIEW_AND_APPROVAL]: {
    defaultEnabled: false,
    defaultHidden: true,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.PUBLISHED]: {
    defaultEnabled: false,
    defaultHidden: true,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.OWNERS]: {
    defaultEnabled: true,
    defaultHidden: true,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.REVIEWERS]: {
    defaultEnabled: false,
    defaultHidden: true,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.ANTICIPATED_RELEASE]: {
    defaultEnabled: false,
    defaultHidden: true,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.OUTCOMES_POPULATION]: {
    defaultEnabled: false,
    defaultHidden: true,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.NOTES]: {
    defaultEnabled: false,
    defaultHidden: true,
    requiredBeforePublish: false
  },
  [FILE_OVERVIEW_SECTIONS.WORKFLOW_STATUS]: {
    defaultEnabled: false,
    defaultHidden: true,
    requiredBeforePublish: false
  }
} as const satisfies Record<FileOverviewSectionKey, FileOverviewSectionSetting>;

export const FILE_OVERVIEW_METRIC_CATEGORY_LABELS = {
  [FILE_OVERVIEW_METRIC_CATEGORIES.OUTCOME]: 'Outcome',
  [FILE_OVERVIEW_METRIC_CATEGORIES.PROCESS]: 'Process',
  [FILE_OVERVIEW_METRIC_CATEGORIES.BALANCING]: 'Balancing'
} as const satisfies Record<FileOverviewMetricCategory, string>;

/** Every overview body, in the editor and in every reader, uses this one editor type. */
export const FILE_OVERVIEW_EDITOR_TYPE = EDITOR_TYPES.RICH_TEXT_BASIC_LINKS;

/**
 * Sections whose value is read live from the file's records rather than authored in the overview:
 * the overview stores only whether each is on it and shown.
 */
export const FILE_OVERVIEW_METADATA_SECTIONS = {
  TAGS: 'tags',
  REVIEW_AND_APPROVAL: 'reviewAndApproval',
  OWNERS: 'owners',
  REVIEWERS: 'reviewers',
  ANTICIPATED_RELEASE: 'anticipatedRelease',
  PUBLISHED: 'published',
  WORKFLOW_STATUS: 'workflowStatus'
} as const;

export type FileOverviewMetadataSectionKey =
  (typeof FILE_OVERVIEW_METADATA_SECTIONS)[keyof typeof FILE_OVERVIEW_METADATA_SECTIONS];

export const FILE_OVERVIEW_METADATA_SECTION_LABELS = {
  [FILE_OVERVIEW_METADATA_SECTIONS.TAGS]: 'Tags',
  [FILE_OVERVIEW_METADATA_SECTIONS.REVIEW_AND_APPROVAL]: 'Review and approval',
  [FILE_OVERVIEW_METADATA_SECTIONS.OWNERS]: 'Owners',
  [FILE_OVERVIEW_METADATA_SECTIONS.REVIEWERS]: 'Reviewers',
  [FILE_OVERVIEW_METADATA_SECTIONS.ANTICIPATED_RELEASE]: 'Anticipated release',
  [FILE_OVERVIEW_METADATA_SECTIONS.PUBLISHED]: 'Published',
  [FILE_OVERVIEW_METADATA_SECTIONS.WORKFLOW_STATUS]: 'Workflow status'
} as const satisfies Record<FileOverviewMetadataSectionKey, string>;

export function isOverviewContentSection(
  section: string
): section is FileOverviewContentSectionKey {
  return (FILE_OVERVIEW_SECTION_ORDER as readonly string[]).includes(section);
}

export function isOverviewMetadataSection(
  section: string
): section is FileOverviewMetadataSectionKey {
  return (Object.values(FILE_OVERVIEW_METADATA_SECTIONS) as readonly string[]).includes(section);
}

/**
 * `errorType` on the 422 a promote answers with when the file is not ready to publish, or when a
 * Live promote needs its program data prompts answered first.
 */
export const PUBLISH_BLOCKER_ERROR_TYPES = {
  MISSING_REQUIRED_TAGS: 'MISSING_REQUIRED_TAGS',
  MISSING_REQUIRED_SECTIONS: 'MISSING_REQUIRED_SECTIONS',
  LIVE_PROMPTS: 'LIVE_PROMPTS'
} as const;

/** The program data changes a Live promote offers. */
export const FILE_LIVE_PROMPTS = {
  CLEAR_ANTICIPATED_RELEASE: 'clearAnticipatedRelease',
  SET_RELEASED: 'setReleased',
  SET_PUBLISHED_TODAY: 'setPublishedToday'
} as const;
export type FileLivePromptKey = (typeof FILE_LIVE_PROMPTS)[keyof typeof FILE_LIVE_PROMPTS];
export type PublishBlockerErrorType =
  (typeof PUBLISH_BLOCKER_ERROR_TYPES)[keyof typeof PUBLISH_BLOCKER_ERROR_TYPES];

/** `errorType` on the 422 adding a file owner answers with when the account cannot author the file. */
export const FILE_OWNER_ERROR_TYPES = {
  NOT_AUTHOR: 'OWNER_NOT_AUTHOR'
} as const;

export function isPublishBlockerErrorType(
  errorType: unknown
): errorType is PublishBlockerErrorType {
  return (Object.values(PUBLISH_BLOCKER_ERROR_TYPES) as unknown[]).includes(errorType);
}

export const FILE_WORKFLOW_STATUSES = {
  IN_DEVELOPMENT: 'in_development',
  IN_REVIEW: 'in_review',
  NEEDS_EHR_BUILD: 'needs_ehr_build',
  READY_FOR_RELEASE: 'ready_for_release',
  RELEASED: 'released',
  ON_HOLD: 'on_hold'
} as const;

export type FileWorkflowStatus =
  (typeof FILE_WORKFLOW_STATUSES)[keyof typeof FILE_WORKFLOW_STATUSES];

export const FILE_WORKFLOW_STATUS_LABELS = {
  [FILE_WORKFLOW_STATUSES.IN_DEVELOPMENT]: 'In Development',
  [FILE_WORKFLOW_STATUSES.IN_REVIEW]: 'In Review',
  [FILE_WORKFLOW_STATUSES.NEEDS_EHR_BUILD]: 'Needs EHR Build',
  [FILE_WORKFLOW_STATUSES.READY_FOR_RELEASE]: 'Ready for Release',
  [FILE_WORKFLOW_STATUSES.RELEASED]: 'Released',
  [FILE_WORKFLOW_STATUSES.ON_HOLD]: 'On Hold'
} as const satisfies Record<FileWorkflowStatus, string>;

export const FILE_WORKFLOW_STATUS_DESCRIPTIONS = {
  [FILE_WORKFLOW_STATUSES.IN_DEVELOPMENT]: 'Being written or revised',
  [FILE_WORKFLOW_STATUSES.IN_REVIEW]: 'Out with reviewers or a committee',
  [FILE_WORKFLOW_STATUSES.NEEDS_EHR_BUILD]: 'Waiting on orders, EHR links, Q&A mapping, etc.',
  [FILE_WORKFLOW_STATUSES.READY_FOR_RELEASE]: 'Built; waiting for the go-live date',
  [FILE_WORKFLOW_STATUSES.RELEASED]: 'Live and in use',
  [FILE_WORKFLOW_STATUSES.ON_HOLD]: 'Paused; not being worked on'
} as const satisfies Record<FileWorkflowStatus, string>;

export const FILE_REVIEW_SCHEDULE_TYPES = {
  INTERVAL: 'interval',
  CUSTOM_DATE: 'customDate'
} as const;

export type FileReviewScheduleType =
  (typeof FILE_REVIEW_SCHEDULE_TYPES)[keyof typeof FILE_REVIEW_SCHEDULE_TYPES];

export const FILE_REVIEW_INTERVAL_PRESET_MONTHS = [6, 12, 18, 24, 36] as const;

export const FILE_REVIEW_INTERVAL_MAX_MONTHS = 120;

export const FILE_APPROVED_BY_MAX_LENGTH = 200;

export const FILE_PROGRAM_DATA_WRITABLE_FIELDS = [
  'workflowStatus',
  'lastFormalReviewOn',
  'approvedBy',
  'reviewSchedule',
  'anticipatedReleaseOn',
  'publishedOn'
] as const;

/**
 * Filter vocabulary for the Studio Author file table (POST /organizations/:organizationId/files/list).
 * Field names match the legacy Author filter builder so saved views carry over unchanged.
 */
export const FILE_LIST_FILTER_FIELDS = {
  FILE_TYPE: 'file-type',
  FILE_NAME: 'file-name',
  PUBLISH_STATUS: 'publish-status',
  MODULE: 'module',
  OWNER: 'owner',
  LAST_UPDATED_AT: 'last-updated-at',
  LAST_UPDATED_BY: 'last-updated-by',
  TAG: 'tag',
  TAG_CATEGORY: 'tag-category'
} as const;

export type FileListFilterField =
  (typeof FILE_LIST_FILTER_FIELDS)[keyof typeof FILE_LIST_FILTER_FIELDS];

export const FILE_LIST_FILTER_OPERATORS = {
  IS: 'is',
  IS_NOT: 'is-not',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'not-contains',
  IS_BEFORE: 'is-before',
  IS_AFTER: 'is-after',
  EMPTY: 'empty',
  NOT_EMPTY: 'not-empty'
} as const;

export type FileListFilterOperator =
  (typeof FILE_LIST_FILTER_OPERATORS)[keyof typeof FILE_LIST_FILTER_OPERATORS];

export const FILE_LIST_FILTER_GROUP_OPERATORS = {
  AND: 'and',
  OR: 'or'
} as const;

export type FileListFilterGroupOperator =
  (typeof FILE_LIST_FILTER_GROUP_OPERATORS)[keyof typeof FILE_LIST_FILTER_GROUP_OPERATORS];

/** Owner filter value meaning "files with no owner". */
export const FILE_LIST_UNASSIGNED_OWNER = 'unassigned';

export const FILE_LIST_SORT_KEYS = {
  NAME: 'name',
  MODULE: 'module',
  TYPE: 'type',
  STATUS: 'status',
  LAST_UPDATED: 'last-updated'
} as const;

export type FileListSortKey = (typeof FILE_LIST_SORT_KEYS)[keyof typeof FILE_LIST_SORT_KEYS];

export const FILE_LIST_SCOPES = {
  ALL: 'all',
  MODULE: 'module',
  FOLDER: 'folder',
  MY_WORK: 'my-work',
  ARCHIVE: 'archive'
} as const;

export type FileListScope = (typeof FILE_LIST_SCOPES)[keyof typeof FILE_LIST_SCOPES];

export const FILE_LIST_MY_WORK_DEFAULT_EDITED_WITHIN_DAYS = 90;
export const FILE_LIST_MY_WORK_MAX_EDITED_WITHIN_DAYS = 3650;

/** Page size bounds for POST /organizations/:organizationId/files/list. */
export const FILE_LIST_DEFAULT_LIMIT = 50;
export const FILE_LIST_MAX_LIMIT = 200;
